import React from 'react';

/**
 * PaperBridge UI rules — one place, used by every page.
 * The full written rulebook is ui-rules/README.md (repo root); keep the two in step.
 *
 *  - The page name appears ONCE, in the top Header (ROUTE_TITLES). Pages do not
 *    repeat it, and add at most one short hint line (13px, gray).
 *  - Font: Poppins everywhere.
 *  - Sizes: section title 11px/600 uppercase (brand blue); labels 11.5px/600;
 *    body and inputs 13px; table cells 12.5px; table header 10.5px/600 uppercase;
 *    stat numbers 28px/600.
 *  - Colours: brand navy #1E3A8A, green #16A34A, amber #F59E0B, red #DC2626;
 *    greys and surfaces from the theme tokens (dark mode follows).
 *  - Width: every page fills the content area (UI.page) — no centred max-width column.
 *  - Shapes: cards, inputs and buttons are rounded 20px; cards have a 1px
 *    gray-200 border and a soft shadow.
 *  - Buttons: primary = navy→green gradient, white; secondary = white with a
 *    gray border; danger = red text and border.
 *  - Tabs: the Configurations underline tabs (tabBar + tabButton).
 *  - Tables: navy header row, white uppercase labels.
 */

export const FONT = "'Poppins', sans-serif";

export const COLORS = {
  navy: '#1E3A8A',
  green: '#16A34A',
  amber: '#F59E0B',
  red: '#DC2626',
  gradient: 'linear-gradient(135deg, #1E3A8A 0%, #16A34A 100%)',
};

export const page: React.CSSProperties = {
  // Full width on every screen (the user's rule): no max width, no centring.
  fontFamily: FONT, padding: '16px 24px', width: '100%', boxSizing: 'border-box',
};

export const hint: React.CSSProperties = {
  margin: '0 0 14px', fontSize: 13, color: 'var(--color-text-secondary, var(--color-gray-500))',
};

export const card: React.CSSProperties = {
  background: 'var(--card-background)', border: '1px solid var(--color-gray-200)',
  borderRadius: 20, padding: 16, marginBottom: 16,
  boxShadow: '0 1px 3px rgba(15,23,42,0.06)', fontFamily: FONT,
};

export const sectionTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: 'var(--color-primary-text)', textTransform: 'uppercase',
  letterSpacing: '0.05em', margin: '0 0 12px', fontFamily: FONT,
};

export const label: React.CSSProperties = {
  display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 4,
};

export const input: React.CSSProperties = {
  padding: '8px 12px', border: '1px solid var(--color-gray-300)', borderRadius: 20, fontSize: 13,
  fontFamily: FONT, width: '100%', boxSizing: 'border-box',
  background: 'var(--card-background)', color: 'var(--color-gray-900)',
};

export const btn: React.CSSProperties = {
  padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: '1px solid var(--color-gray-300)', background: 'var(--card-background)',
  color: 'var(--color-gray-700)', fontFamily: FONT, whiteSpace: 'nowrap',
};

export const btnPrimary: React.CSSProperties = {
  ...btn, background: COLORS.gradient, color: '#FFFFFF', border: 'none',
};

/** Page-toolbar buttons (Report, Reassign, Create …): one size and weight,
 *  icon + text. The outline one is navy; the primary one keeps a transparent
 *  border so both are the same height. */
export const btnToolbar: React.CSSProperties = {
  ...btn, display: 'inline-flex', alignItems: 'center', gap: 6,
  border: '1px solid #1E3A8A', color: 'var(--color-primary-text)',
};
export const btnToolbarPrimary: React.CSSProperties = {
  ...btnToolbar, background: COLORS.gradient, color: '#FFFFFF', border: '1px solid transparent',
};

export const btnDanger: React.CSSProperties = {
  ...btn, color: '#B91C1C', borderColor: '#FCA5A5',
};

export const tabBar: React.CSSProperties = {
  display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16,
  borderBottom: '1px solid var(--color-border-light, var(--color-gray-200))',
};

export const tabButton = (active: boolean): React.CSSProperties => ({
  padding: '12px 18px', fontSize: 13, fontWeight: active ? 600 : 500, fontFamily: FONT,
  color: active ? 'var(--color-primary)' : 'var(--color-text-secondary, var(--color-gray-500))',
  background: 'transparent', border: 'none',
  borderBottom: `2px solid ${active ? 'var(--color-primary)' : 'transparent'}`,
  marginBottom: -1, cursor: 'pointer', whiteSpace: 'nowrap',
});

export const th: React.CSSProperties = {
  textAlign: 'left', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
  color: '#FFFFFF', background: COLORS.navy, padding: '9px 12px', whiteSpace: 'nowrap',
};

export const td: React.CSSProperties = {
  fontSize: 12.5, padding: '8px 12px', borderTop: '1px solid var(--color-gray-100)',
  color: 'var(--color-gray-800)', verticalAlign: 'middle',
};
/** The row's name column (first column of a list table): 600, gray-900. */
export const tdName: React.CSSProperties = { ...td, fontWeight: 600, color: 'var(--color-gray-900)' };
/** Small action button inside a table row (Edit, Delete …) — keeps rows slim. */
export const btnRow: React.CSSProperties = {
  padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  border: '1px solid var(--color-gray-300)', background: 'var(--card-background)',
  color: 'var(--color-gray-700)', fontFamily: "'Poppins', sans-serif", whiteSpace: 'nowrap',
};

/** "Nothing here" state: one card, text centred both ways, optional action
 *  (e.g. Refresh) centred under the text. Every empty list uses this. */
/**
 * THE empty state (one look everywhere — use components/EmptyState.tsx):
 * a card, text centred 13px gray-500, an optional action (e.g. ↻ Refresh) under it.
 */
export const empty: React.CSSProperties = {
  ...card, padding: '40px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', gap: 12,
  fontSize: 13, fontWeight: 400, color: 'var(--color-text-secondary, var(--color-gray-500))',
};
/** Same text, no card — for an empty list that already sits inside a card. */
export const emptyFlat: React.CSSProperties = {
  ...empty, background: 'transparent', border: 'none', boxShadow: 'none', marginBottom: 0, padding: '32px 16px',
};
/** Same text inside a table body: <tr><td colSpan={n} style={UI.emptyCell}>…</td></tr>. */
export const emptyCell: React.CSSProperties = {
  padding: '32px 16px', textAlign: 'center', fontSize: 13, fontWeight: 400,
  color: 'var(--color-text-secondary, var(--color-gray-500))',
};
